import { BucketAttachment, BucketImage } from ".";
import { WithI18n } from "../../i18n";
import { BookingInfoDto } from "./Booking";

export enum ServiceCompatiblePlaceType {
  APARTMENT = 0,
  AMENITY = 1,
}

export const serviceCompatiblePlaceTypes = {
  [ServiceCompatiblePlaceType.APARTMENT]: "appartamento",
  [ServiceCompatiblePlaceType.AMENITY]: "amenity",
};

export interface ServiceCompatiblePlaceAmenity {
  id: string;
  name: string;
}

export interface ServiceCompatiblePlace {
  type: ServiceCompatiblePlaceType;
  amenity?: ServiceCompatiblePlaceAmenity;
}

export interface ServiceBuilding {
  id: string;
  name: string;
  /**
   * Establishes whether, for this building, this service is compatible with
   * something else other than the apartment.
   */
  compatiblePlaces?: ServiceCompatiblePlace[];
  overrideProps?: ServiceOverridableProps;
}

export interface ServiceFormQuestion extends WithI18n<"title"> {
  required: boolean;
}

export interface ServiceFormQuestionGqlInputModel extends ServiceFormQuestion {
  sortIndex: number;
}

export interface ServiceFormQuestionUIModel
  extends ServiceFormQuestionGqlInputModel {
  deleted: boolean;
}

export type ServiceOverridableProps = Partial<
  Omit<Service, "id" | "buildings" | "weekSlots" | "providedBy">
>;

export enum ServiceType {
  DEFAULT = 0,
  /**
   * Represents services that follows the Car Wash logic:
   * - Slots with max 1 unit per slot with a fixed duration.
   * - List items not categorized
   * - Payment mandatory.
   */
  CARWASH = 1,
  /**
   * Represents services that follows the Longevity logic:
   * - Slots have a max amount of reservable professional people, per category.
   * - List items are categorized. Each category needs to have the definition of how many persons are available per slot.
   * - Available slots are calculated with the sum of reserved list items duration.
   * - The amount of how many persons are available per slot can be updated programmatically and for each single slot / day.
   * - Payment is mandatory.
   */
  LONGEVITY = 2,
  MAMACLEAN = 3,
  BEVY = 4,
  TAC = 5,
  /**
   * Represents a service that works with the generic behavior of the Titiro
   * Service Definition.
   */
  GENERIC_COMPOSABLE = 999,
}

export enum ComposableServiceConfigPaymentMode {
  PAY_IMMEDIATELY = "pay_immediately",
  PRE_AUTHORIZE = "pre_authorize",
  PAY_AFTER = "pay_after",
  QUOTATION_ONLY = "quotation_only",
}

export type ComposableServiceConfig = {
  displayServiceDescription: boolean;
  displayListItemsInHomepage: boolean;
  displayPhoneNumberCta: boolean;
  displayAttachments: boolean;
  askAvailabilitySlots: boolean;
  askServices: boolean;
  askProducts: boolean;
  askVehicleInfo: boolean;
  askAmenityPlace: boolean;
  paymentMode: ComposableServiceConfigPaymentMode;
  paymentReloadInPercentage?: number;
} & WithI18n<"paymentReloadInPercentageReason">;

export type ServiceTimeSlotReservationOptions = {
  /**
   * Represents the list item category that belongs to this service reservation
   * type.
   */
  listItemCategoryName?: string;
  /**
   * Represents the amount of bookable units in this slots.
   *
   * Used in service type = LONGEVITY to handle the amount of persons that can
   * be booked in this slot.
   */
  bookableUnits?: number;
};

export type ServiceTimeSlot = {
  reservationTimeOffsetInHours?: number;
  fromTime?: number;
  toTime?: number;
  durationInMinutes?: number;
  maxUnits?: number;
  reservationOptions?: ServiceTimeSlotReservationOptions[];
};

export type ServiceProvidedBy = {
  id: string;
  name: string;
};

export enum ServiceDaySlotAvailabilityOptionsModes {
  /**
   * The amount of needed persons per slot will be calculated by considering
   * always one per category.
   *
   * Example
   * if (unique) categories are:
   * ['viso e corpo', 'bellezza']
   *
   * the amount of needed persons will be 2 (one per category).
   */
  ONE_PER_CATEGORY = 0,
  /**
   * The amount of needed persons per slot will be calculated by considering
   * always one unit per slot, regardless whether the selected items requires
   * one or more categories (or zero).
   */
  NTH_PER_SLOT = 1,
}

export const serviceDaySlotAvailabilityOptions = {
  [ServiceDaySlotAvailabilityOptionsModes.ONE_PER_CATEGORY]:
    "Un professionista per categoria",
  [ServiceDaySlotAvailabilityOptionsModes.NTH_PER_SLOT]:
    "Un professionista per slot",
};

export interface ServiceDaySlotAvailabilityOptions {
  mode: ServiceDaySlotAvailabilityOptionsModes;
}

export interface ServiceLaundryOptions {
  washAndFoldDetailLink?: string;
  forbiddenDates?: number[];
}

export interface Service
  extends WithI18n<
    | "name"
    | "description"
    | "infoBoxContent"
    | "reservationDeleteConditions"
    | "reservationUsefulInfoBoxContent"
    | "reservationOkPushMessage"
    | "reservationKoPushMessage"
  > {
  id: string;
  buildings: ServiceBuilding[];
  name: string;
  description: string;
  phone?: string;
  image?: BucketImage;
  attachments?: BucketAttachment[];
  type: ServiceType;
  infoBoxTitle?: String;
  infoBoxContent?: string;
  weekSlots?: ServiceTimeSlot[][];
  daySlotAvailabilityOptions?: ServiceDaySlotAvailabilityOptions;
  reservationDeleteConditions?: string;
  reservationUsefulInfoBoxContent?: string;
  stripePublicApiKey?: string;
  stripePrivateApiKey?: string;
  providedBy?: ServiceProvidedBy;
  active: boolean;
  alertEmail?: string;
  displayInApp: boolean;
  termsAndConditionLink?: string;
  privacyPolicyLink?: string;
  bevyTimeSlotInUtc?: string;
  /**
   * These informations are relative to this service in case it's a laundry
   * service type. This field was needed due to the addition of many links in-app
   * that are related to the service provider.
   */
  laundryOptions?: ServiceLaundryOptions;
  composableOptions?: ComposableServiceConfig;
  reservationSurvey?: [ServiceFormQuestion];
  hideSlotEndTime?: boolean;
  bookingInfo?: BookingInfoDto;
  serviceCategories?: string[];
  featured?: boolean;
  tacUrl?: string;
  tacServiceId?: string;
  tacQuotationId?: string;
  sortIndex?: number;
}

export const serviceTypeOpts = {
  [ServiceType.DEFAULT]: "Default",
  [ServiceType.CARWASH]: "Car-Wash",
  [ServiceType.LONGEVITY]: "Longevity",
  [ServiceType.MAMACLEAN]: "Mama Clean",
  [ServiceType.BEVY]: "Bevy Express",
  [ServiceType.TAC]: "Tac!",
  [ServiceType.GENERIC_COMPOSABLE]: "Generico (componibile)",
};
